import React, { useEffect, useMemo, useState } from "react";
import FileUpload from "components/UI/FileUpload";
import { axiosCitajteo } from "common/api/axios/citajteo_axios";
import { FILE_UP_NAME } from "constant/file/file_constants";
import styled from "styled-components";
import { DlUnit } from "model/dl_unit/DlUnit";
import UnitScanFileNames from "./UnitScanFileNames";
import AppLink from "components/common/style/link/AppLink";
import { apiStaticUrlGet } from "common/api/api_common";
import { splitPath } from "common/util/file/file_common";

type UnitScanFileEditProps = {
  unit: DlUnit;
};

const UnitScanFileEdit = ({ unit }: UnitScanFileEditProps) => {
  const initialFoldName = useMemo(() => unit.fold, [unit]);
  const [fileNames, setFileNames] = useState<string[]>(unit.scans);
  const [foldName, setFoldName] = useState<string>(initialFoldName);
  const [showUpdate, setShowUpdate] = useState<boolean>(false);

  useEffect(() => {
    const fileNamesChanged =
      JSON.stringify(fileNames) !== JSON.stringify(unit.scans);

    if (fileNamesChanged && !showUpdate) {
      setShowUpdate(true);
    }
  }, [fileNames, unit.scans, showUpdate]);

  const fileUploadIndHandle = (data: any, ind: number) => {
    // console.log("fileUploadIndHandle", data, ind);
    const filePathParts = splitPath(data.files[0].path);
    if (filePathParts.length >= 3) {
      const uploadedFoldName = filePathParts.slice(-3, -1).join("/");
      if (uploadedFoldName !== foldName) {
        setFoldName(uploadedFoldName);
      }
    }
    const newFileNames = data.files.map(
      (file: { filename: string }) => file.filename,
    );
    const fNames = [
      ...fileNames.slice(0, ind),
      ...newFileNames,
      ...fileNames.slice(ind),
    ];
    setFileNames(fNames);
  };

  const scanApiUpUrl = `dl-unit/${unit.id}/scan/file`;

  const resetFileNames = () => {
    setFileNames(unit.scans);
    setShowUpdate(false);
  };

  const removeFileName = (fileNameIndex: number) => {
    setFileNames((currentFileNames) =>
      currentFileNames.filter((_, index) => index !== fileNameIndex),
    );
  };

  return (
    <Div>
      <div>Skenovi</div>
      <FileNamesNavDiv>
        {fileNames.length > 0 && (
          <>
            {fileNames.map((fileName, index) => (
              <React.Fragment key={`${fileName}-${index}`}>
                <FileUpload
                  axiosInstance={axiosCitajteo}
                  fileFieldName={FILE_UP_NAME}
                  fileUploadHandle={(data: any) =>
                    fileUploadIndHandle(data, index)
                  }
                  uploadUrl={scanApiUpUrl}
                  multiple
                  upLabel=""
                />
                <FileNameNavDiv>
                  {foldName ? (
                    <AppLink
                      to={apiStaticUrlGet(
                        `dl-unit/scan/${foldName}/${fileName}`,
                      )}
                      target="_blank"
                    >
                      {fileName}
                    </AppLink>
                  ) : (
                    <span>{fileName}</span>
                  )}
                  <RemoveFileNameButton
                    type="button"
                    onClick={() => removeFileName(index)}
                    aria-label={`Obriši sken ${fileName}`}
                  >
                    ×
                  </RemoveFileNameButton>
                </FileNameNavDiv>
              </React.Fragment>
            ))}
          </>
        )}
        <FileUpload
          axiosInstance={axiosCitajteo}
          fileFieldName={FILE_UP_NAME}
          fileUploadHandle={(data: any) =>
            fileUploadIndHandle(data, fileNames.length)
          }
          uploadUrl={scanApiUpUrl}
          multiple
          upLabel=""
        />
      </FileNamesNavDiv>

      {showUpdate && (
        <UnitScanFileNames
          unitId={unit.id}
          fileNames={fileNames}
          setFileNames={setFileNames}
          resetFileNames={resetFileNames}
        />
      )}
    </Div>
  );
};

const Div = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FileNamesNavDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  flex-wrap: wrap;
`;

const FileNameNavDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const RemoveFileNameButton = styled.button`
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0;
`;

export default UnitScanFileEdit;
